import { motion } from 'framer-motion'
import { fontSizes } from 'front-end/src/components/chakraThemeExtension'
import {
  StatusDraftGrey,
  StatusOpenGrey
} from 'front-end/src/components/common/status/Status'
import { CertificationItemTypes } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import React, { FC } from 'react'
import { brandColors } from 'shared/basicPalette'

import { Box, Flex, Text } from '@chakra-ui/react'
import { Plural, Trans } from '@lingui/macro'

import { TypeOfElement } from '../../../../../types/generic'
import IconCalendar from '../../../components/common/icon/IconCalendar'
import IconDoc from '../../../components/common/icon/IconDoc'
import IconMap from '../../../components/common/icon/IconMap'
import Tooltip from '../../../components/common/tooltip/Tooltip'
import { countWidthRate } from '../../surveys/helpers'
import { ManageCertificationsPageQuery } from '../ManageCertificationsPage.codegen'
import { Badge, CertificationName } from './CertificationEnrolmentCard'

import './CertificationManageCard.less'

type Certification = TypeOfElement<
  NonNullable<ManageCertificationsPageQuery['admin']>['certifications']
>

const countItems = (
  items: NonNullable<Certification>['items'],
  type: CertificationItemTypes
) => {
  return items?.filter((i) => i.item_type === type).length || 0
}

export const CertificationResultBar = ({ invitedCount, completedCount }) => {
  const animationForCompleted = {
    initial: {
      width: '0%'
    },
    animate: {
      width: `${countWidthRate(completedCount, invitedCount)}`
    }
  }

  return (
    <div className="CertificationResultBar">
      <div className="CertificationResultBar__value">
        <span className="CertificationResultBar__value__label">
          <Trans>Done</Trans>
        </span>
        <span className="CertificationResultBar__value__number green">
          {completedCount}
        </span>
      </div>
      <div className="CertificationResultBar__stripe">
        <motion.span
          className="CertificationResultBar__stripe__responses"
          {...animationForCompleted}
        />
      </div>
      <div className="CertificationResultBar__value">
        <span className="CertificationResultBar__value__label">
          <Trans>Invited</Trans>
        </span>
        <span className="CertificationResultBar__value__number">
          {invitedCount}
        </span>
      </div>
    </div>
  )
}

export const CertificationSetup: FC<{
  isDraft: boolean
  topicsCount: number
  eventsCount
  pathwaysCount: number
}> = ({ isDraft, topicsCount, pathwaysCount, eventsCount }) => {
  return (
    <Flex mt="8px" alignItems="center" flexShrink={0} flexGrow={1}>
      {isDraft ? <StatusDraftGrey /> : <StatusOpenGrey />}

      <Flex color={brandColors.grey.light} ml="8px" mt="2px">
        {!!topicsCount && (
          <Tooltip
            delay={1000}
            text={
              <Plural
                value={topicsCount}
                one="# resource"
                other="# resources"
              />
            }
          >
            <Flex mr={4}>
              <Text lineHeight="16px">
                <IconDoc size="sm" />
              </Text>
              <Text pl={1}>{topicsCount}</Text>
            </Flex>
          </Tooltip>
        )}
        {!!pathwaysCount && (
          <Tooltip
            delay={1000}
            text={
              <Plural
                value={pathwaysCount}
                one="# Pathway"
                other="# Pathways"
              />
            }
          >
            <Flex mr={4}>
              <Text lineHeight="16px">
                <IconMap />
              </Text>
              <Text pl={1}>{pathwaysCount}</Text>
            </Flex>
          </Tooltip>
        )}
        {!!eventsCount && (
          <Tooltip
            delay={1000}
            text={<Plural value={eventsCount} one="# Event" other="# Events" />}
          >
            <Flex mr={4}>
              <Text lineHeight="16px">
                <IconCalendar />
              </Text>
              <Text pl={1}>{eventsCount}</Text>
            </Flex>
          </Tooltip>
        )}
      </Flex>
    </Flex>
  )
}

export const CertificationManageCard: FC<{
  certification: Certification
}> = ({ certification }) => {
  const { items } = certification

  const topicsCount = countItems(items, CertificationItemTypes.topic)
  const pathwaysCount = countItems(items, CertificationItemTypes.pathway)
  const eventsCount = countItems(items, CertificationItemTypes.event)

  return (
    <Flex
      borderRadius="8px"
      h="100px"
      m="8px"
      p={0}
      width="auto"
      bg={brandColors.white}
      css={{ position: 'relative' }}
      className="Card"
    >
      <Badge isExpired={false} progress={null} />
      <Flex
        fontSize={fontSizes.sm}
        w="100%"
        p="16px 0"
        justifyContent="space-between"
      >
        <Flex
          fontSize={fontSizes.sm}
          direction="column"
          p="16px"
          justifyContent="center"
        >
          <Box
            fontSize={
              certification.name.length > 40 ? fontSizes.lg : fontSizes['2xl']
            }
          >
            <CertificationName>{certification.name}</CertificationName>
          </Box>
          <CertificationSetup
            isDraft={!certification.locked_at}
            pathwaysCount={pathwaysCount}
            topicsCount={topicsCount}
            eventsCount={eventsCount}
          />
        </Flex>
        <CertificationResultBar
          invitedCount={certification.enrolmentsCount}
          completedCount={certification.completedEnrolmentsCount}
        />
      </Flex>
    </Flex>
  )
}
